import { useEffect, useState, type FC } from "react";
import { createPortal } from "react-dom";
import {
  Cancel01Icon,
  ComputerIcon,
  SmartPhone01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { Tabs } from "shared/components/BaseTabs";
import { Loader } from "shared/components/Loader";
import { cx } from "shared/utils";
import { useAuthContext } from "providers/Auth";
import type { OwnerScreeningReport } from "api/owner-screening-report";
import { DownloadPdfButton } from "./DownloadPdfButton";
import { ReportBody } from "./ReportBody";

type ViewMode = "desktop" | "mobile";

/** Mirrored in index.css, which uses these to print the report on its own. */
const PREVIEW_OPEN_CLASS = "owner-report-preview-open";
const PREVIEW_CLASS = "owner-report-preview";

/**
 * The owner's view, shown to a partner before anything is shared. Both previews
 * use it — the share dialog's, built from this application, and the setup
 * page's, built from a sample — because they are the same thing to look at and
 * differ only in where the report came from.
 *
 * Full screen rather than a modal, the same frame as the landlord survey
 * builder's preview (FR 6): a toolbar with Close and Desktop / Mobile, and the
 * owner page underneath it at the width the owner will actually read it.
 */
export const ReportPreview: FC<{
  opened: boolean;
  onClose: () => void;
  report?: OwnerScreeningReport;
  isLoading?: boolean;
  isError?: boolean;
  /** Named opposite the tabs, e.g. "Sample application". */
  label?: string;
  /** People left out because their reports are still running. */
  excludedPeople?: string[];
  /** The note that would go with the share — the setup page samples one. */
  message?: string | null;
}> = ({
  opened,
  onClose,
  report,
  isLoading,
  isError,
  label,
  excludedPeople = [],
  message
}) => {
  const { auth, partner } = useAuthContext();
  const [viewMode, setViewMode] = useState<ViewMode>("desktop");

  // Download PDF prints the page, and the app is still mounted behind this
  // overlay — without the marker the print carries whatever page the partner
  // opened the preview from. Paired with the @media print rules in index.css.
  useEffect(() => {
    if (!opened) return undefined;

    document.body.classList.add(PREVIEW_OPEN_CLASS);
    return () => document.body.classList.remove(PREVIEW_OPEN_CLASS);
  }, [opened]);

  if (!opened) return null;

  const isMobile = viewMode === "mobile";
  // Nothing has been shared yet, so the attribution is what it *would* say: this
  // partner user, today. The owner's own page gets both from the link instead.
  const sharedBy = auth?.name?.trim().split(/\s+/)[0];
  const partnerName = partner?.name;
  const logoUrl = partner?.logo_url;

  return createPortal(
    <div
      className={cx(
        PREVIEW_CLASS,
        "fixed inset-0 z-[100] flex flex-col bg-background-1000",
        // A fixed box does not paginate, so printing would capture only the
        // viewport. Released here the same way the owner's own page does it.
        "print:static print:h-auto print:overflow-visible"
      )}
    >
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-alpha-80 px-6 print:hidden">
        <Button
          HugeIcon={Cancel01Icon}
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={onClose}
        >
          Close
        </Button>

        <Tabs
          size="sm"
          onChange={(mode: ViewMode) => setViewMode(mode)}
          tabStyle="ghost"
          active={viewMode}
          tabs={[
            {
              label: "Desktop",
              value: "desktop",
              leftElement: { type: "icon" as const, component: ComputerIcon }
            },
            {
              label: "Mobile",
              value: "mobile",
              leftElement: {
                type: "icon" as const,
                component: SmartPhone01Icon
              }
            }
          ]}
        />

        {/* Balances the Close button so the tabs stay centred even with no label. */}
        <span className="min-w-[72px] text-right text-sm text-content-400">
          {label}
        </span>
      </header>

      <div className="flex flex-grow justify-center overflow-hidden bg-background-800 print:block print:overflow-visible">
        <div
          className={cx(
            // No background of its own: the report brings the page grey with
            // it, so the preview and the owner's own page cannot disagree.
            "flex flex-col overflow-y-auto transition-all duration-300",
            // The scroll container clips everything past the viewport, so it is
            // released for print — and the Mobile frame drops its device
            // chrome, since a printed 375px column is nobody's intent.
            "print:h-auto print:w-full print:max-w-full print:overflow-visible",
            "print:m-0 print:rounded-none print:border-0 print:shadow-none",
            isMobile
              ? "my-6 h-[calc(100%-48px)] w-[375px] max-w-full rounded-2xl border border-alpha-100 shadow-lg"
              : "h-full w-full"
          )}
        >
          <Show if={Boolean(isLoading)}>
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          </Show>

          <Show if={Boolean(isError)}>
            <p className="py-16 text-center text-sm text-content-400">
              The preview could not be built. Try again in a moment.
            </p>
          </Show>

          {report ? (
            <div className="w-full">
              <ReportBody
                report={report}
                compact={isMobile}
                partnerName={partnerName}
                sharedBy={sharedBy}
                logoUrl={logoUrl}
                message={message}
                actions={<DownloadPdfButton />}
              />
              {/* Repeated here because the preview is where a partner notices
                  somebody is missing. */}
              <Show if={excludedPeople.length > 0}>
                <p className="mx-6 mb-8 rounded-lg bg-warning-50 px-4 py-3 text-sm text-content-700">
                  Not included yet, because their reports are still running:{" "}
                  {excludedPeople.join(", ")}. Share again once they finish.
                </p>
              </Show>
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};
